import { ISelection } from "src/constants/interfaces/bet";
import { BetTypeEnum } from "src/constants/enums/betType";

/**
 * Returns the unique set of game ids that have at least one bet builder
 * selection. Each entry is a "bet builder card" in the betslip UI.
 */
export const getBetBuilderGameIds = (selections: ISelection[]): Set<number> => {
  const ids = new Set<number>();
  selections.forEach((selection) => {
    if (selection.event.isInternalBetBuilder) ids.add(selection.game.id);
  });
  return ids;
};

/**
 * A bet builder card needs at least two legs to be a valid combination.
 * Returns true when ANY card in the slip currently has only one BB-tagged
 * selection — the user has started a card but hasn't completed it.
 */
export const hasIncompleteBetBuilderCard = (selections: ISelection[]): boolean => {
  const bbSelections = selections.filter((s) => s.event.isInternalBetBuilder);
  if (!bbSelections.length) return false;

  const countByGame = new Map<number, number>();
  bbSelections.forEach((s) => {
    countByGame.set(s.game.id, (countByGame.get(s.game.id) || 0) + 1);
  });

  for (const count of countByGame.values()) {
    if (count < 2) return true;
  }
  return false;
};

/**
 * Returns true when a regular (non-BB) selection in the betslip has an
 * incompatible `express_id` with a bet builder card from the same game.
 *
 * Background: `getEventsCompatibilityOmitter` auto-drops conflicting
 * regulars on click, but it explicitly SKIPS `isInternalBetBuilder`
 * selections. So a BB card + a regular pick from the same game with a
 * conflicting `express_id` (same value, or either side missing) sneaks
 * into the slip without triggering the omitter.
 *
 * Sportsbook intentionally allows the user to click such events (no UI
 * block). The betslip surfaces the conflict: notify-message warning at
 * the bottom + place-bet disabled while it persists.
 */
export const hasExpressIdIncompatibility = (selections: ISelection[]): boolean => {
  const bbLegsByGame = new Map<number, ISelection[]>();
  const regularsByGame = new Map<number, ISelection[]>();

  selections.forEach((sel) => {
    const gameId = sel.game.id as number;
    const bucket = sel.event.isInternalBetBuilder ? bbLegsByGame : regularsByGame;
    if (!bucket.has(gameId)) bucket.set(gameId, []);
    bucket.get(gameId)!.push(sel);
  });

  for (const [gameId, bbLegs] of bbLegsByGame) {
    const regulars = regularsByGame.get(gameId);
    if (!regulars?.length) continue;

    for (const reg of regulars) {
      const regExp = reg.market.express_id;
      for (const leg of bbLegs) {
        const legExp = leg.market.express_id;
        const missing = !regExp || !legExp;
        const same = regExp === legExp;
        if (missing || same) return true;
      }
    }
  }

  return false;
};

/** True if any selection is a bet builder selection. */
export const hasBetBuilderSelections = (selections: ISelection[]): boolean =>
  selections.some((s) => s.event.isInternalBetBuilder);

/**
 * True when the betslip is exactly one bet builder card and nothing else
 * (no regular selections, no other bet builder games).
 */
export const isSingleBetBuilderCard = (selections: ISelection[]): boolean => {
  if (!selections.length) return false;
  const everyIsBetBuilder = selections.every((s) => s.event.isInternalBetBuilder);
  if (!everyIsBetBuilder) return false;
  return getBetBuilderGameIds(selections).size === 1;
};

/**
 * Effective selection count for bet-type decisions, max-count rules and the
 * tab badge:
 *   - all bet builder selections from the same game collapse to ONE entry
 *   - every regular selection counts as ONE
 *
 * Example: 1 bet builder card with 5 events + 1 regular single  →  count is 2.
 */
export const getEffectiveSelectionsCount = (selections: ISelection[]): number => {
  const betBuilderGameIds = getBetBuilderGameIds(selections);
  const regularCount = selections.filter((s) => !s.event.isInternalBetBuilder).length;
  return betBuilderGameIds.size + regularCount;
};

/**
 * Effective banker count: a BB card counts as banker if any of its legs is
 * flagged banker (a card behaves like a single leg downstream, so any banker
 * leg implies the whole card is banker). Regular bankers count one each.
 */
export const getEffectiveBankerCount = (selections: ISelection[]): number => {
  const bbBankerGames = new Set<number>();
  let regular = 0;
  selections.forEach((s) => {
    if (s.event.isInternalBetBuilder) {
      if (s.isBanker) bbBankerGames.add(s.game.id);
    } else if (s.isBanker) {
      regular += 1;
    }
  });
  return regular + bbBankerGames.size;
};

/**
 * Resolve the bet type to send to the place-bet swarm command.
 *
 *   - one bet builder card (no other selections)        → InternalBetBuilder (51)
 *   - bet builder + others, UI picked System (≥3 legs)  → System (preserve UI choice)
 *   - bet builder mixed with regular OR multiple cards  → Multiple (2)
 *   - only regular selections                            → UI-selected bet type
 *
 * The System branch was previously collapsed into Multiple unconditionally,
 * which caused `do_bet_terminal` to send `type: 2` even when the user had
 * selected System. The backend handles System+BB by treating each BB card
 * as a single virtual leg (via `bet_builder_match_id` on the selections)
 * + the standard `sys_bet` combination count.
 */
export const resolveBetType = (
  selections: ISelection[],
  uiBetType: BetTypeEnum
): BetTypeEnum => {
  if (isSingleBetBuilderCard(selections)) return BetTypeEnum.InternalBetBuilder;
  if (hasBetBuilderSelections(selections)) {
    if (uiBetType === BetTypeEnum.System && getEffectiveSelectionsCount(selections) >= 3) {
      return BetTypeEnum.System;
    }
    return BetTypeEnum.Multiple;
  }
  return uiBetType;
};

/**
 * Effective prices for total-odd / possible-win calculation:
 *   - each bet builder game contributes ONE price (its combined odd)
 *   - each regular selection contributes its own price
 *
 * Returns an empty array if any bet builder group is missing its combined
 * price (e.g. waiting on get_bet_builder_odds), so callers can show "-".
 */
export const getEffectivePrices = (
  selections: ISelection[],
  betBuilderGroups: Record<number, { combinedPrice: number }>
): number[] => {
  const seenGames = new Set<number>();
  const prices: number[] = [];
  for (const s of selections) {
    if (s.event.isInternalBetBuilder) {
      if (seenGames.has(s.game.id)) continue;
      seenGames.add(s.game.id);
      const combined = betBuilderGroups[s.game.id]?.combinedPrice;
      if (!combined) return [];
      prices.push(combined);
    } else if (s.event.price) {
      prices.push(s.event.price);
    }
  }
  return prices;
};

export const getEffectiveTotalOdd = (
  selections: ISelection[],
  betBuilderGroups: Record<number, { combinedPrice: number }>
): number => {
  const prices = getEffectivePrices(selections, betBuilderGroups);
  if (!prices.length) return 0;
  return prices.reduce((acc, p) => acc * p, 1);
};

/**
 * Flatten bet builder groups into single synthetic selections so the standard
 * calculation pipeline (`getBetCalculations`) handles them correctly. Each BB
 * card becomes ONE pseudo-event whose price is the group's combined price;
 * the `isInternalBetBuilder` flag is stripped so downstream code treats it
 * like any other multiple leg.
 *
 * This is what lets bonus rules, free bets, and tax work for BB without
 * teaching the calc engine about BB cards: the engine just sees N regular
 * legs with the right total odd.
 *
 * Returns `null` if any BB group is missing its combined price (e.g. waiting
 * on `get_bet_builder_odds`) — caller should treat that as "no win yet".
 */
export const getEffectiveSelectionsForCalc = (
  selections: ISelection[],
  betBuilderGroups: Record<number, { combinedPrice: number }>
): ISelection[] | null => {
  const seenGames = new Set<number>();
  const out: ISelection[] = [];
  for (const s of selections) {
    if (s.event.isInternalBetBuilder) {
      if (seenGames.has(s.game.id)) continue;
      seenGames.add(s.game.id);
      const combined = betBuilderGroups[s.game.id]?.combinedPrice;
      if (!combined) return null;
      out.push({
        ...s,
        alternateOdd: 0,
        event: {
          ...s.event,
          price: combined,
          isInternalBetBuilder: false,
          sp: false,
          eventOption: undefined,
        },
      });
    } else {
      out.push(s);
    }
  }
  return out;
};
